<template>
  <div class="page-card">
    <h2 class="page-title">商家用户</h2>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          placeholder="手机号/商家名称/品牌名称"
          clearable
          style="width: 300px"
        />
        <el-select v-model="query.userStatus" clearable placeholder="状态" style="width: 140px">
          <el-option label="正常" :value="1" />
          <el-option label="封禁" :value="0" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
      </div>
    </div>

    <el-table :data="list" border v-loading="loading">
      <el-table-column prop="merchantName" label="商家名称" min-width="180" />
      <el-table-column prop="brandName" label="品牌名称" min-width="160" />
      <el-table-column label="手机号" width="150">
        <template slot-scope="{ row }">
          {{ maskMobile(row.mobile) }}
        </template>
      </el-table-column>
      <el-table-column prop="contactName" label="联系人" width="140" />
      <el-table-column label="状态" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="row.userStatus === 1 ? 'success' : 'danger'">
            {{ row.userStatus === 1 ? '正常' : '封禁' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="180" />
      <el-table-column label="操作" width="220">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">查看详情</el-button>
          <el-button v-if="row.userStatus === 1" type="text" @click="changeStatus(row, 'ban')">封禁</el-button>
          <el-button v-else type="text" @click="changeStatus(row, 'unban')">解封</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page.sync="query.page"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="fetchData"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog title="商家详情" :visible.sync="detailVisible" width="900px">
      <div v-if="detail.userId">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="状态">{{ detail.userStatus === 1 ? '正常' : '封禁' }}</el-descriptions-item>
          <el-descriptions-item label="商家名称">{{ detail.merchantName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="品牌名称">{{ detail.brandName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ maskMobile(detail.mobile) }}</el-descriptions-item>
          <el-descriptions-item label="联系人">{{ detail.contactName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系人手机号">{{ maskMobile(detail.contactMobile) }}</el-descriptions-item>
          <el-descriptions-item label="微信">{{ detail.contactWechat || '-' }}</el-descriptions-item>
          <el-descriptions-item label="认证状态">{{ authStatusText(detail.authStatus) }}</el-descriptions-item>
          <el-descriptions-item label="商家类型">{{ detail.merchantType || '-' }}</el-descriptions-item>
          <el-descriptions-item label="简介" :span="2">{{ detail.intro || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-divider>实名信息</el-divider>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="认证状态">{{ authStatusText(detail.authStatus) }}</el-descriptions-item>
          <el-descriptions-item label="认证类型">{{ authTypeText(detail.authType) }}</el-descriptions-item>
          <el-descriptions-item label="主体/公司名称">{{ detail.authCompanyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="法人姓名">{{ detail.authLegalPersonName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ detail.authSubmittedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审核时间">{{ detail.authAuditedAt || '-' }}</el-descriptions-item>
          <el-descriptions-item label="驳回原因" :span="2">{{ detail.authRejectReason || '-' }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="authMaterialList.length" class="auth-material-grid">
          <div v-for="item in authMaterialList" :key="item.url" class="auth-material-card">
            <div class="auth-material-label">{{ item.label }}</div>
            <el-image
              :src="resolveMediaUrl(item.url)"
              :preview-src-list="authPreviewImages"
              fit="cover"
              class="auth-material-image"
            />
          </div>
        </div>
        <el-empty v-else description="暂无实名材料" :image-size="88" />

        <el-divider>照片资料</el-divider>
        <div v-if="merchantImageList.length" class="media-grid">
          <el-image
            v-for="item in merchantImageList"
            :key="item.id || item.mediaUrl"
            :src="item.mediaUrl"
            :preview-src-list="merchantPreviewImages"
            fit="cover"
            class="media-image"
          />
        </div>
        <el-empty v-else description="暂无照片资料" :image-size="88" />

        <el-divider>视频资料</el-divider>
        <div v-if="merchantVideoList.length" class="media-grid video-grid">
          <div v-for="item in merchantVideoList" :key="item.id || item.mediaUrl" class="video-card">
            <video
              class="media-video"
              :src="item.mediaUrl"
              :poster="item.coverUrl || ''"
              controls
              preload="metadata"
            />
            <div class="video-actions">
              <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无视频资料" :image-size="88" />
      </div>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="960px"
      top="6vh"
      append-to-body
      @closed="handleVideoPreviewClosed"
    >
      <div class="video-preview-wrap">
        <video
          v-if="previewVideo.mediaUrl"
          ref="previewVideoPlayer"
          class="preview-video-player"
          :src="previewVideo.mediaUrl"
          :poster="previewVideo.coverUrl || ''"
          controls
          autoplay
          preload="auto"
        />
      </div>
      <span slot="footer">
        <el-button @click="videoPreviewVisible = false">关闭</el-button>
        <el-button type="primary" @click="requestVideoFullscreen">全屏播放</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  banMerchantUser,
  getMerchantUserDetail,
  getMerchantUsers,
  unbanMerchantUser
} from '../../../api/platformManage'
import { isImageMedia, isVideoMedia, maskMobile, normalizeMediaItem, resolveMediaUrl } from '../../../utils/format'

export default {
  name: 'MerchantUsersPage',
  data() {
    return {
      list: [],
      total: 0,
      loading: false,
      detailVisible: false,
      detail: {},
      query: {
        keyword: '',
        userStatus: undefined,
        page: 1,
        pageSize: 10
      },
      videoPreviewVisible: false,
      previewVideo: {}
    }
  },
  computed: {
    merchantMediaList() {
      if (Array.isArray(this.detail.mediaList) && this.detail.mediaList.length) {
        return this.detail.mediaList
          .map(item => normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item))
          .filter(Boolean)
      }
      const list = []
      if (this.detail.logoUrl) {
        list.push(normalizeMediaItem({
          mediaType: 'image',
          mediaUrl: this.detail.logoUrl
        }))
      }
      return list.filter(Boolean)
    },
    merchantImageList() {
      return this.merchantMediaList.filter(item => isImageMedia(item))
    },
    merchantVideoList() {
      return this.merchantMediaList.filter(item => isVideoMedia(item))
    },
    merchantPreviewImages() {
      return this.merchantImageList.map(item => item.mediaUrl).filter(Boolean)
    },
    authMaterialList() {
      return [
        { label: '营业执照/主体资料', url: this.detail.authDocumentUrl },
        { label: '许可证资料', url: this.detail.authLicenseNoUrl },
        { label: '身份证人像面', url: this.detail.authCardFrontUrl },
        { label: '身份证国徽面', url: this.detail.authCardBackUrl }
      ].filter(item => item.url)
    },
    authPreviewImages() {
      return this.authMaterialList.map(item => resolveMediaUrl(item.url)).filter(Boolean)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    maskMobile,
    resolveMediaUrl,
    resolveUserId(row) {
      return row && row.userId != null ? String(row.userId) : ''
    },
    authStatusText(status) {
      const map = {
        0: '待审核',
        1: '审核通过',
        2: '审核拒绝',
        3: '已撤回'
      }
      return map[Number(status)] || '-'
    },
    authTypeText(type) {
      const map = {
        1: '企业认证',
        2: '个体工商户认证',
        3: '个人认证'
      }
      return map[Number(type)] || (type != null ? String(type) : '-')
    },
    async fetchData() {
      this.loading = true
      try {
        const data = await getMerchantUsers(this.query)
        this.list = data.records || []
        this.total = Number(data.total || 0)
        this.query.page = Number(data.page || this.query.page)
        this.query.pageSize = Number(data.pageSize || this.query.pageSize)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.query.page = 1
      this.fetchData()
    },
    handleSizeChange() {
      this.query.page = 1
      this.fetchData()
    },
    async openDetail(row) {
      this.detail = await getMerchantUserDetail(this.resolveUserId(row))
      this.detailVisible = true
    },
    async changeStatus(row, action) {
      const isBan = action === 'ban'
      try {
        await MessageBox.confirm(`确认${isBan ? '封禁' : '解封'}该商家用户吗？`, '操作确认', {
          type: 'warning',
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      const userId = this.resolveUserId(row)
      if (isBan) {
        await banMerchantUser(userId)
      } else {
        await unbanMerchantUser(userId)
      }
      this.$message.success(`${isBan ? '封禁' : '解封'}成功`)
      await this.fetchData()
      if (this.detailVisible && String(this.detail.userId) === userId) {
        this.detailVisible = false
        this.detail = {}
      }
    },
    openVideoPreview(item) {
      this.previewVideo = {
        mediaUrl: item.mediaUrl || '',
        coverUrl: item.coverUrl || ''
      }
      this.videoPreviewVisible = true
    },
    handleVideoPreviewClosed() {
      const player = this.$refs.previewVideoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
        player.currentTime = 0
      }
      this.previewVideo = {}
    },
    async requestVideoFullscreen() {
      const player = this.$refs.previewVideoPlayer
      if (!player) return
      const requestFullscreen =
        player.requestFullscreen ||
        player.webkitRequestFullscreen ||
        player.mozRequestFullScreen ||
        player.msRequestFullscreen
      if (!requestFullscreen) {
        this.$message.warning('当前浏览器不支持全屏播放')
        return
      }
      await requestFullscreen.call(player)
    }
  }
}
</script>

<style scoped>
.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.video-grid {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.media-image {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  background: #f5f7fa;
}

.auth-material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.auth-material-card {
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}

.auth-material-label {
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}

.auth-material-image {
  width: 100%;
  height: 130px;
  border-radius: 6px;
  background: #f5f7fa;
}

.video-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.media-video {
  width: 100%;
  height: 180px;
  display: block;
  border-radius: 8px;
  background: #111827;
  object-fit: contain;
}

.video-actions {
  display: flex;
  justify-content: flex-end;
}

.video-preview-wrap {
  display: flex;
  justify-content: center;
}

.preview-video-player {
  width: 100%;
  max-height: 72vh;
  border-radius: 10px;
  background: #111827;
}
</style>
